import styled from 'styled-components';
import { Button, FlexView, Row, Text } from '@cuemath/comps';

import { TAG_VARIANT } from './constants';

export const Container = styled(FlexView)`
  && {
    display: flex;
    flex-direction: row;
    height: 100%;
  }
`;

export const LeftPanel = styled(FlexView)`
  && {
    width: 300px;
    min-width: 300px;
    border-right: 1px solid #eee;
    overflow-y: auto;
    padding: 12px 0;
  }
`;

export const RightPanel = styled(FlexView)`
  && {
    flex: 1;
    padding: 20px 24px;
    overflow-y: auto;
  }
`;

export const EmptyState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #808080;
  font-size: 14px;
`;

// Tree components
export const TreeContainer = styled.div`
  padding: 0 8px;
`;

export const TreeNode = styled.div`
  user-select: none;
`;

export const NodeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  padding-left: ${({ $depth }) => 8 + $depth * 16}px;
  cursor: pointer;
  border-radius: 4px;
  background: ${({ $selected }) => ($selected ? '#E8F0FE' : 'transparent')};

  &:hover {
    background: ${({ $selected }) => ($selected ? '#E8F0FE' : '#f5f5f5')};
  }
`;

export const ExpandIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  font-size: 10px;
  color: #808080;
  transition: transform 0.15s ease;
  transform: ${({ $expanded }) => ($expanded ? 'rotate(90deg)' : 'rotate(0deg)')};
`;

export const NodeLabel = styled.span`
  font-size: 13px;
  color: ${({ $isCategory }) => ($isCategory ? '#555' : '#2b2b2b')};
  font-weight: ${({ $isCategory }) => ($isCategory ? '600' : '400')};
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const ChildrenContainer = styled.div`
  display: ${({ $expanded }) => ($expanded ? 'block' : 'none')};
`;

export const CategoryEditIcon = styled.span`
  font-size: 12px;
  color: #aaa;
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.15s ease;

  ${NodeRow}:hover & {
    opacity: 1;
  }
`;

export const InlineInput = styled.input`
  font-size: 13px;
  font-weight: 600;
  color: #555;
  flex: 1;
  padding: 0 4px;
  margin: -2px 0;
  border: 1px solid #0177ff;
  border-radius: 3px;
  outline: none;
  background: #fff;
  min-width: 0;
  box-sizing: border-box;
`;

export const CategoryBadge = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: #666;
  background: #f0f0f0;
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 2px 8px;
`;

// Form components
export const FormContainer = styled.div`
  max-width: 640px;
`;

export const FormHeader = styled(Row)`
  && {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 8px;
  }
`;

export const PathText = styled(Text)`
  && {
    font-size: 12px;
    color: #808080;
    margin-bottom: 20px;
    font-family: monospace;
  }
`;

export const Field = styled.div`
  margin-bottom: 16px;
`;

export const Label = styled(Text)`
  && {
    display: block;
    font-size: 12px;
    font-weight: 600;
    color: #555;
    margin-bottom: 4px;
  }
`;

export const Input = styled.input`
  width: 100%;
  padding: 8px 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #0177ff;
  }
`;

export const TextArea = styled.textarea`
  width: 100%;
  padding: 8px 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  font-family: inherit;
  resize: vertical;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #0177ff;
  }
`;

export const FormSelect = styled.select`
  width: 100%;
  padding: 8px 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  background: #fff;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #0177ff;
  }
`;

export const Toggle = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-size: 14px;
  color: #2b2b2b;

  input {
    width: 16px;
    height: 16px;
    cursor: pointer;
  }
`;

export const ButtonRow = styled(Row)`
  && {
    display: flex;
    gap: 10px;
    margin-top: 24px;
  }
`;

export const NewButton = styled(Button)`
  && {
    margin: 10px 12px;
    padding: 8px;
    font-size: 13px;
    font-weight: 600;
    color: #0177ff;
    background: transparent;
    border: 1px dashed #0177ff;
    border-radius: 4px;
    cursor: pointer;
    width: calc(100% - 24px);
    height: auto;
  }

  && > * {
    border: none;
    padding: 0;
    background: transparent;
  }

  &&:hover {
    background: #f0f7ff;
    border: 1px dashed #0177ff;
  }
`;

export const DeleteButton = styled(Button)`
  && {
    padding: 8px 16px;
    font-size: 13px;
    font-weight: 600;
    color: #d32f2f;
    background: transparent;
    border: 1px solid #d32f2f;
    border-radius: 4px;
    cursor: pointer;
    height: auto;
    transition: opacity 0.15s ease;
  }

  && > * {
    border: none;
    padding: 0;
    background: transparent;
  }
  &&:hover {
    background: #fde8e8;
    border: 1px solid #d32f2f;
    opacity: 0.85;
  }

  &&:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// KB Scope multi-select components
export const ScopeDropdownWrapper = styled.div`
  position: relative;
`;

export const ScopeDropdownTrigger = styled.div`
  width: 100%;
  padding: 8px 10px;
  border: 1px solid ${({ $open }) => ($open ? '#0177ff' : '#ddd')};
  border-radius: 4px;
  font-size: 14px;
  background: #fff;
  box-sizing: border-box;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: ${({ $hasValue }) => ($hasValue ? '#2b2b2b' : '#999')};
  user-select: none;
`;

export const ScopeDropdownMenu = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  max-height: 200px;
  overflow-y: auto;
  background: #fff;
  border: 1px solid #ddd;
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  z-index: 10;
`;

export const ScopeDropdownItem = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  font-size: 13px;
  color: #2b2b2b;
  cursor: pointer;

  &:hover {
    background: #f5f5f5;
  }

  input {
    width: 14px;
    height: 14px;
    cursor: pointer;
  }
`;

export const ScopeChipsContainer = styled(Row)`
  && {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 8px;
  }
`;

export const ScopeChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 10px;
  font-size: 12px;
  font-weight: 500;
  color: #0177ff;
  background: #e8f0fe;
  border: 1px solid #c4dafc;
  border-radius: 12px;
`;

export const ScopeChipRemove = styled.span`
  cursor: pointer;
  font-size: 14px;
  line-height: 1;
  color: #0177ff;
  opacity: 0.6;
  transition: opacity 0.15s ease;

  &:hover {
    opacity: 1;
  }
`;

export const SaveButton = styled(Button)`
  && {
    padding: 8px 16px;
    font-size: 13px;
    font-weight: 600;
    color: #0177ff;
    background: transparent;
    border: 1px solid #0177ff;
    border-radius: 4px;
    cursor: pointer;
    height: auto;
    transition: opacity 0.15s ease;
  }

  && > * {
    border: none;
    padding: 0;
    background: transparent;
  }

  &&:hover {
    opacity: 0.85;
    border: 1px solid #0177ff;
  }
  &&:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// Confirm modal components
// TODO: Migrate to centralized modal registry (src/modules/modal/index.js)
// when chatbot modals need to be reused outside this module.
export const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

export const ModalBox = styled.div`
  background: #fff;
  border-radius: 8px;
  padding: 24px;
  max-width: 400px;
  width: 90%;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.15);
`;

export const ModalTitle = styled(Text)`
  && {
    font-size: 15px;
    font-weight: 600;
    color: #2b2b2b;
    margin-bottom: 8px;
  }
`;

export const ModalText = styled(Text)`
  && {
    font-size: 13px;
    color: #666;
    margin-bottom: 20px;
  }
`;

export const ModalActions = styled(Row)`
  && {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
  }
`;

export const ModalCancelButton = styled(Button)`
  && {
    padding: 8px 16px;
    font-size: 13px;
    font-weight: 600;
    color: #555;
    background: #f0f0f0;
    border: 1px solid #ddd;
    border-radius: 4px;
    cursor: pointer;
    height: auto;
  }

  && > * {
    border: none;
    padding: 0;
    background: transparent;
  }
  &&:hover {
    background: #e5e5e5;
    border: 1px solid #ddd;
  }
`;

export const ModalConfirmDeleteButton = styled(Button)`
  && {
    padding: 8px 16px;
    font-size: 13px;
    font-weight: 600;
    color: #fff;
    background: #ea5959;
    border: 1px solid #ea5959;
    border-radius: 4px;
    cursor: pointer;
    height: auto;
  }

  && > * {
    border: none;
    padding: 0;
    background: transparent;
  }
  &&:hover {
    opacity: 0.85;
    border: 1px solid #ea5959;
  }
`;

export const FormTitle = styled(Text)`
  && {
    margin: 0;
    font-size: 16px;
    font-weight: bold;
  }
`;

export const HelpText = styled(Text)`
  && {
    font-size: 11px;
    color: #888;
    margin-top: 4px;
  }
`;

export const WarningBox = styled.div`
  background: #fff8e1;
  border: 1px solid #ffcc02;
  border-radius: 6px;
  padding: 10px 14px;
  margin-bottom: 16px;
`;

export const WarningTitle = styled.div`
  font-weight: 600;
  font-size: 13px;
  color: #e65100;
  margin-bottom: 4px;
`;

export const WarningItem = styled.div`
  font-size: 12px;
  color: #795548;
`;

export const DropdownArrow = styled.span`
  font-size: 10px;
  color: #999;
`;

export const DropdownEmptyItem = styled.div`
  color: #999;
  cursor: default;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  font-size: 13px;
`;

export const RequiredMark = styled.span`
  color: #e65100;
`;

export const SecondaryButtonRow = styled(Row)`
  && {
    display: flex;
    gap: 10px;
    margin-top: 12px;
  }
`;

export const CategoryHelpText = styled.p`
  font-size: 13px;
  color: #808080;
  margin: 0;
`;

export const MultiFormSelect = styled(FormSelect)`
  min-height: 80px;
`;

export const TreeSpacer = styled.span`
  width: 16px;
  display: inline-block;
`;

export const ListSummary = styled.div`
  padding: 8px 16px 4px;
  font-size: 11px;
  color: #808080;
  letter-spacing: 0.02em;
`;

export const FilterToggle = styled(Row)`
  && {
    display: flex;
    align-self: stretch;
    flex-shrink: 0;
    margin: 0 12px 4px;
    border: 1px solid #ddd;
    border-radius: 6px;
    overflow: hidden;
  }
`;

export const FilterOption = styled(FlexView)`
  && {
    flex: 1;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    padding: 6px 4px;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    user-select: none;
    border-right: 1px solid #ddd;
    background: ${({ $active }) => ($active ? '#0177ff' : '#fff')};
    color: ${({ $active }) => ($active ? '#fff' : '#555')};
  }

  &&:last-child {
    border-right: none;
  }

  &&:hover {
    background: ${({ $active }) => ($active ? '#0177ff' : '#f0f7ff')};
  }
`;

// Small P / T / P·T chip on each leaf, derived from `user_types`.
// Parent = green, Teacher = amber, Both = purple.
const TAG_COLORS = {
  [TAG_VARIANT.PARENT]: { fg: '#0a5c2e', bg: '#dff5e6' },
  [TAG_VARIANT.TEACHER]: { fg: '#8a5a00', bg: '#fff3d6' },
  [TAG_VARIANT.BOTH]: { fg: '#5a3d8a', bg: '#efe6fb' },
};

export const UserTypeTag = styled(Text)`
  && {
    font-size: 9px;
    font-weight: 700;
    line-height: 1;
    padding: 2px 5px;
    border-radius: 3px;
    letter-spacing: 0.03em;
    color: ${({ $variant }) =>
      (TAG_COLORS[$variant] || TAG_COLORS[TAG_VARIANT.PARENT]).fg};
    background: ${({ $variant }) =>
      (TAG_COLORS[$variant] || TAG_COLORS[TAG_VARIANT.PARENT]).bg};
  }
`;
